if(!customElements.get('bundle-info')) {

  class BundleInfo extends HTMLElement {

    get products() {
      return Array.from(this.querySelectorAll('bundle-product')).filter(product => {
        const hasSize = product.querySelector('[data-type="size"] input:checked');
        return hasSize;
      })
    }

    constructor() {
      super();
    }

    connectedCallback() {
      this.productsCount = this.dataset.productsCount;
      this.bundleDiscount = this.dataset.bundleDiscount;
      this.addToCartButton = this.querySelector('[data-bundle-add-to-cart]');
      this.bundleId = this.dataset.bundleId;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') || document.querySelector('mini-cart');

      this.setListeners();
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber && this.variantChangeUnsubscriber();
    }

    setListeners() {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.bundleProductRendered,
        this.onVariantChange.bind(this)
      );

      this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
    }

    async handleAddToCart() {
      const items = [];

      this.products.forEach((product) => {
        const checkedSizeInput = product.querySelector('[data-type="size"] input:checked');

        if (!checkedSizeInput) return;

        items.push({
          id: Number(checkedSizeInput.dataset.variantId),
          quantity: 1,
          properties: {
            _bundleId: `${this.bundleId}`
          }
        });
      });

      if (items.length === 0 || !this.cart) return;

      this.cart.setActiveElement(document.activeElement);

      const body = {
        items,
        sections: this.cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!response.ok) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'bundle-info',
          productVariantId: items[0].id,
          errors: result.errors || result.description,
          message: result.message
        });
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: items[0].id,
        cartData: result
      });

      this.cart.classList.contains('is-empty') && this.cart.classList.remove('is-empty');
      this.cart.renderContents(result);
    }

    formatMoney(cents) {
      return `${window.money.symbol}${(cents / 100).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
    
    onVariantChange() {
      const comparePrice = this.products.reduce((acc, product) => {
        return acc + Number(product.querySelector('[name="price"]').value);
      }, 0);
    
      const newPrice = comparePrice * (1 - this.bundleDiscount / 100);
    
      this.addToCartButton.querySelector('[data-price]').innerHTML = this.formatMoney(newPrice);
      this.addToCartButton.querySelector('[data-compare-price]').innerHTML = this.formatMoney(comparePrice);
      this.addToCartButton.toggleAttribute('disabled', this.products.length != this.productsCount);
    }
  }

  customElements.define('bundle-info', BundleInfo);

}

if(!customElements.get('bundle-product')) {
  class BundleProduct extends HTMLElement {

    get productId() {
      return this.dataset.productId;
    }

    constructor() {
      super();
    }
  
    connectedCallback() {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.onVariantChange.bind(this)
      );
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber && this.variantChangeUnsubscriber();
    }

    onVariantChange(event) {
      const { target, selectedOptionValues, type } = event.data;
      if (target?.closest('bundle-product') !== this) return;

      const optionValues = type === 'color'
        ? [target.dataset.optionValueId]
        : selectedOptionValues;

      let requestUrl = ProductUtils.buildRequestUrlWithParams(this.dataset.url, null, optionValues);
      type === 'color' && (requestUrl += '&bundle_color_only=1');

      this.render(requestUrl);
    }

    async render(url) {
      const response = await fetch(`${url}&view=bundle-product`);
      const result = await response.text();

      const html = new DOMParser().parseFromString(result, 'text/html');
      this.outerHTML = html.querySelector('bundle-product').outerHTML;

      publish(PUB_SUB_EVENTS.bundleProductRendered, { product: this });
    }

  }

  customElements.define('bundle-product', BundleProduct);
}

if(!customElements.get('bundle-product-info')) {

  class BundleProductInfo extends HTMLElement {

    constructor() {
      super();
      this.dialog = null;
      this.content = null;
      this.bundleInfo = null;
      this.activeTrigger = null;
      this.activeBundleProduct = null;
      this.highlightTimeout = null;
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-bundle-product-info-dialog]');
      this.content = this.querySelector('[data-bundle-product-info-content]');
      this.bundleInfo = this.previousElementSibling;

      this.bundleInfo.addEventListener('click', (event) => {
        const trigger = event.target.closest('bundle-details-trigger');
        if (!trigger) return;

        event.preventDefault();
        this.open(trigger);
      });

      this.bundleInfo.addEventListener('keydown', (event) => {
        if (event.code !== 'Enter' && event.code !== 'Space') return;

        const trigger = event.target.closest('bundle-details-trigger');
        if (!trigger) return;

        event.preventDefault();
        this.open(trigger);
      });

      this.dialog.addEventListener('click', (event) => {
        const actionBtn = event.target.closest('[data-bundle-product-info-action]');

        if (actionBtn) {
          actionBtn.dataset.action === 'select-size' ? this.scrollToProduct() : this.close();
          return;
        }

        event.target.closest('[data-bundle-product-info-close]') && this.close();
        event.target === this.dialog && this.close();
      });

      this.dialog.addEventListener('close', () => {
        this.activeTrigger?.focus();
        this.activeTrigger = null;
        this.activeBundleProduct = null;
      });
    }

    async open(trigger) {
      const handle = trigger.dataset.handle;
      const bundleProduct = trigger.closest('bundle-product');

      if (!handle || !bundleProduct) return;

      this.activeTrigger = trigger;
      this.activeBundleProduct = bundleProduct;
      this.toggleAttribute('loading', true);

      const optionValues = Array.from(
        bundleProduct.querySelectorAll('variant-selects fieldset input:checked:not([data-no-size])')
      ).map(({ dataset }) => dataset.optionValueId).filter(Boolean);

      const params = ['view=bundle-product-info'];
      optionValues.length && params.push(`option_values=${optionValues.join(',')}`);

      const response = await fetch(`${window.Shopify.routes.root}products/${handle}?${params.join('&')}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const panel = html.querySelector('[data-bundle-product-info-panel]');

      panel && (this.content.innerHTML = panel.outerHTML);
      this.setFooterAction(bundleProduct);

      this.toggleAttribute('loading', false);
      this.dialog.showModal();
    }

    setFooterAction(bundleProduct) {
      const btn = this.dialog.querySelector('[data-bundle-product-info-action]');
      const hasSize = !!bundleProduct.querySelector('[data-type="size"] input:checked');

      btn.dataset.action = hasSize ? 'continue' : 'select-size';
      btn.textContent = hasSize ? btn.dataset.continueLabel : btn.dataset.selectSizeLabel;
    }

    scrollToProduct() {
      const bundleProduct = this.activeBundleProduct;
      this.close();

      if (!bundleProduct) return;

      bundleProduct.scrollIntoView({ behavior: 'smooth', block: 'center' });

      for (const product of document.querySelectorAll('bundle-product[highlighted]')) {
        product.toggleAttribute('highlighted', false);
      }

      bundleProduct.toggleAttribute('highlighted', true);
      clearTimeout(this.highlightTimeout);
      this.highlightTimeout = setTimeout(() => {
        bundleProduct.toggleAttribute('highlighted', false);
      }, 3000);
    }

    close() {
      this.dialog.close();
    }
  }

  customElements.define('bundle-product-info', BundleProductInfo);
}